// @ts-nocheck

/* eslint-disable sonarjs/no-duplicate-string */

import { uniqueId as _uniqueId } from 'lodash';
import { BEGIN, COMMIT, REVERT } from 'redux-optimist';

import { FavoriteActionTypes } from '../types/actions/favorite';
import type {
  SetFavoritesAction,
  FavoritesOptimistAction,
  SetFavoritesDispatchAction,
  FavoritesOptimistNoPayloadAction,
  DoPurgeFavoritesAction,
  ReceiveResponseAction,
} from '../types/actions/favorite';

/**
 * Create an action to set the favorites
 *
 * @param  {Array}             The favorites's list array
 * @return {Object}            Return an action object
 */
export const setFavorites: SetFavoritesAction = (matchIds) => ({
  type: FavoriteActionTypes.RECEIVE,
  payload: { matchIds },
});

/**
 * Create an action to add a favorite
 *
 * @param  {string} options.transactionId The transaction identifier
 * @param  {number} options.matchId       The match id
 * @return {Object}                       Return an action object
 */
export const addFavorite: FavoritesOptimistAction = ({ transactionId, matchId }) => ({
  type: FavoriteActionTypes.ADD,
  payload: { matchId },
  optimist: { type: BEGIN, id: transactionId },
});

/**
 * Create an action to inform that the favorite add request successed
 *
 * @param  {string} options.transactionId The transaction identifier
 * @param  {number} options.matchId       The match id
 * @return {Object}                       Return an action object
 */
export const addFavoriteOk: FavoritesOptimistAction = ({ transactionId, matchId }) => ({
  type: FavoriteActionTypes.ADD_OK,
  payload: { matchId },
  optimist: { type: COMMIT, id: transactionId },
});

/**
 * Create an action to inform that the favorite add request failed
 * @param  {string} options.transactionId The transaction identifier
 * @param  {number} options.matchId       The match id
 * @return {Object}                       Return an action object
 */
export const addFavoriteKo: FavoritesOptimistAction = ({ transactionId, matchId }) => ({
  type: FavoriteActionTypes.ADD_KO,
  payload: { matchId },
  optimist: { type: REVERT, id: transactionId },
});

/**
 * Create a function that dispatch some async actions related to the
 * favorite add request
 *
 * @param  {number}   options.matchId The match id
 * @return {function}                 The add function
 */
export const doAddFavorite: SetFavoritesDispatchAction =
  ({ matchId }) =>
  (dispatch) => {
    const transactionId = _uniqueId('redux-optimist-');
    dispatch(addFavorite({ matchId, transactionId }));
  };

/**
 * Create an action to remove a favorite
 *
 * @param  {string} options.transactionId The transaction identifier
 * @param  {number} options.matchId       The match id
 * @return {Object}                       Return an action object
 */
export const delFavorite: FavoritesOptimistAction = ({ transactionId, matchId }) => ({
  type: FavoriteActionTypes.DEL,
  payload: { matchId },
  optimist: { type: BEGIN, id: transactionId },
});

/**
 * Create an action to inform that the favorite remove request successed
 *
 * @param  {string} options.transactionId The transaction identifier
 * @param  {number} options.matchId       The match id
 * @return {Object}                       Return an action object
 */
export const delFavoriteOk: FavoritesOptimistAction = ({ transactionId, matchId }) => ({
  type: FavoriteActionTypes.DEL_OK,
  payload: { matchId },
  optimist: { type: COMMIT, id: transactionId },
});

/**
 * Create an action to inform that the favorite remove request failed
 * @param  {string} options.transactionId The transaction identifier
 * @param  {number} options.matchId       The match id
 * @return {Object}                       Return an action object
 */
export const delFavoriteKo: FavoritesOptimistAction = ({ transactionId, matchId }) => ({
  type: FavoriteActionTypes.DEL_KO,
  payload: { matchId },
  optimist: { type: REVERT, id: transactionId },
});

/**
 * Create a function that dispatch some async actions related to the
 * favorite remove request
 *
 * @param  {number}   options.matchId The match id
 * @return {function}                 The add function
 */
export const doDelFavorite: SetFavoritesDispatchAction =
  ({ matchId }) =>
  (dispatch) => {
    const transactionId = _uniqueId('redux-optimist-');
    dispatch(delFavorite({ matchId, transactionId }));
  };

/**
 * Create an action to purge all favorites
 *
 * @param  {string} options.transactionId The transaction identifier
 * @return {Object}                       Return an action object
 */
export const purgeFavorites: FavoritesOptimistNoPayloadAction = ({ transactionId }) => ({
  type: FavoriteActionTypes.PURGE,
  optimist: { type: BEGIN, id: transactionId },
});

/**
 * Create an action to inform that the favorite purge request successed
 *
 * @param  {string} options.transactionId The transaction identifier
 * @return {Object}                       Return an action object
 */
export const purgeFavoritesOk: FavoritesOptimistNoPayloadAction = ({ transactionId }) => ({
  type: FavoriteActionTypes.PURGE_OK,
  optimist: { type: COMMIT, id: transactionId },
});

/**
 * Create an action to inform that the favorite purge request failed
 * @param  {string} options.transactionId The transaction identifier
 * @return {Object}                       Return an action object
 */
export const purgeFavoritesKo: FavoritesOptimistNoPayloadAction = ({ transactionId }) => ({
  type: FavoriteActionTypes.PURGE_KO,
  optimist: { type: REVERT, id: transactionId },
});

/**
 * Create a function that dispatch some async actions related to the
 * favorite purge request
 *
 * @return {function}                 The add function
 */
export const doPurgeFavorites: DoPurgeFavoritesAction = () => (dispatch) => {
  const transactionId = _uniqueId('redux-optimist-');
  dispatch(purgeFavorites({ transactionId }));
};

/**
 * Middleware action to enhance payload
 * @param  {string} payload.id      The unique request id
 * @param  {Object} payload.result  The result object
 * @return {Object}                 Return an action object
 */
export const receiveResponse: ReceiveResponseAction = (payload) => ({
  type: FavoriteActionTypes.RECEIVE_RESPONSE,
  payload,
});

export default {
  setFavorites,
  addFavorite,
  addFavoriteOk,
  addFavoriteKo,
  doAddFavorite,
  delFavorite,
  delFavoriteOk,
  delFavoriteKo,
  doDelFavorite,
  purgeFavoritesOk,
  purgeFavoritesKo,
  doPurgeFavorites,
  receiveResponse,
};
